import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import {
  Button,
  Flex,
  Flow,
  Input,
  Label,
  Link,
  Panel,
  PhoneNumber,
} from '@xometry/xometry_loft';

import { convertToQuoteSubdomain } from '../../utils/helpers';
import { CustomerRole } from './CustomerRole';
import quotesApi from '../../services/quotes';
import { WWW_ORIGIN } from '../../config';

import {
  requiredMessage,
  firstName,
  lastName,
  zip,
  phone,
  email,
  buyerPassword,
} from '../../utils/validations';

import { buyerRegistrationPanelStyles } from './BuyerRegistrationPanel.styled';

const validationSchema = yup.object().shape({
  firstName: firstName.required(requiredMessage),
  lastName: lastName.required(requiredMessage),
  phone: phone.required(requiredMessage),
  wemail: email.required(requiredMessage),
  wpassword: buyerPassword,
  zipCode: zip,
  termsAccepted: yup
    .boolean()
    .test(
      'truthy',
      'Please agree to the General Terms and Conditions',
      (v) => v
    ),
});

const BuyerRegistrationPanel = (props) => {
  const {
    anonFirst,
    anonLast,
    anonPhoneNumber,
    currentUserHasPassword,
    destination,
    handleRegister,
    prefilledEmail,
    handleRedirect,
    senderName,
    temporaryPasswordKey,
  } = props;
  const [hasPassword] = useState(currentUserHasPassword);

  const registerNewUser = async (
    email,
    buyerPassword,
    firstName,
    lastName,
    companyName,
    phone,
    zip,
    termsAccepted,
    customerRole,
    customerRoleOther,
    dnb,
    onComplete,
    onFail
  ) => {
    const formData = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      company_name: companyName,
      email_address: email,
      phone_number: phone,
      zip_code: zip,
      terms: termsAccepted,
      customer_role:
        customerRole === 'Other' && customerRoleOther
          ? customerRoleOther
          : customerRole,
      dnb,
    };

    if (!hasPassword) {
      formData.password = buyerPassword;
    }

    if (temporaryPasswordKey) {
      formData.temporary_password_key = temporaryPasswordKey;
    }

    if (destination) {
      formData.destination = destination;
    }

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    formData.next = urlParams.get('next') || '';
    formData.mbsy = urlParams.get('mbsy') || '';
    formData.campaignid = urlParams.get('campaignid') || '';
    formData.segmentid = urlParams.get('segmentid') || '';

    try {
      const response = await handleRegister(formData);
      if (response.errors) {
        onFail(response.errors);
      } else {
        await quotesApi.refreshAuthToken();
        const { redirectUrl } = response;
        if (redirectUrl === undefined) {
          onComplete(convertToQuoteSubdomain('/quote'));
        }
        onComplete(convertToQuoteSubdomain(redirectUrl));
      }
    } catch (err) {
      if (err.json && err.json.errors) {
        onFail(err.json.errors);
      } else {
        console.error(err);
      }
    }
  };

  let initialValues = {
    firstName: localStorage.getItem('anonFirst') || anonFirst,
    lastName: localStorage.getItem('anonLast') || anonLast,
    companyName: '',
    phone: localStorage.getItem('anonPhoneNumber') || anonPhoneNumber,
    zipCode: '',
    wemail: prefilledEmail,
    wpassword: '',
    termsAccepted: true,
    customerRole: '',
    customerRoleOther: '',
  };
  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, bag) => {
        const { setSubmitting, setFieldError } = bag;
        setSubmitting(true);

        registerNewUser(
          values.wemail,
          values.wpassword,
          values.firstName.trim(),
          values.lastName.trim(),
          values.companyName,
          values.phone,
          values.zipCode,
          values.termsAccepted,
          values.customerRole,
          values.customerRoleOther,
          window.dnb,
          (redirectUrl) => {
            setSubmitting(false);
            handleRedirect(redirectUrl);
          },
          (errors) => {
            setSubmitting(false);
            Object.keys(errors).forEach((key) => {
              let fieldKey = '';
              if (key === 'email' || key === 'password') {
                fieldKey = `w${key}`; // formik's value should match field id
              }
              if (key === 'terms') {
                fieldKey = 'termsAccepted';
              }
              setFieldError(fieldKey, errors[key]);
            });
          }
        );
      }}
      render={({
        values,
        touched,
        errors,
        handleSubmit,
        handleBlur,
        handleChange,
        setFieldValue,
        setTouched,
        isSubmitting,
      }) => (
        <Form id="leadForm">
          <Panel>
            <div css={buyerRegistrationPanelStyles.title}>
              {prefilledEmail && !senderName
                ? 'Complete Registration'
                : 'Create an Account'}
            </div>
            <div
              style={{
                fontSize: `${senderName ? '16px' : '13px'}`,
                marginTop: `${senderName ? '12px' : 0}`,
                padding: `${senderName ? '0 30px 0 30px' : '0'}`,
                lineHeight: '20px',
                marginBottom: '12px',
                textAlign: 'center',
              }}
            >
              {senderName
                ? `To view your quote from ${senderName}, create an account
                                using the same email address that received the quote.`
                : 'Looking to become a partner?\u00A0'}
              {!senderName && (
                <Link href={`${WWW_ORIGIN}/manufacturing-partner-network`}>
                  Sign up here.
                </Link>
              )}
            </div>
            <Flex>
              <div css={buyerRegistrationPanelStyles.inputField}>
                <Label size="medium" htmlFor="firstName">
                  First Name
                </Label>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={values.firstName}
                  onChange={handleChange}
                  invalid={
                    touched.firstName &&
                    Object.prototype.hasOwnProperty.call(errors, 'firstName')
                  }
                  disabled={isSubmitting}
                  onBlur={handleBlur}
                  className="pii-encrypt"
                />
                {touched.firstName && errors.firstName && (
                  <span css={buyerRegistrationPanelStyles.invalid}>
                    {errors.firstName}
                  </span>
                )}
              </div>
              <div css={buyerRegistrationPanelStyles.inputField}>
                <Label size="medium" htmlFor="lastName">
                  Last Name
                </Label>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={values.lastName}
                  onChange={handleChange}
                  invalid={
                    touched.lastName &&
                    Object.prototype.hasOwnProperty.call(errors, 'lastName')
                  }
                  disabled={isSubmitting}
                  onBlur={handleBlur}
                  className="pii-encrypt"
                />
                {touched.lastName && errors.lastName && (
                  <span css={buyerRegistrationPanelStyles.invalid}>
                    {errors.lastName}
                  </span>
                )}
              </div>
            </Flex>
            <Flex>
              <div css={buyerRegistrationPanelStyles.inputField}>
                <Label size="medium" htmlFor="companyName">
                  Company Name (optional)
                </Label>
                <Input
                  id="companyName"
                  name="companyName"
                  type="text"
                  value={values.companyName}
                  onChange={handleChange}
                  invalid={
                    touched.companyName &&
                    Object.prototype.hasOwnProperty.call(errors, 'companyName')
                  }
                  disabled={isSubmitting}
                  onBlur={handleBlur}
                />
              </div>
              <div css={buyerRegistrationPanelStyles.inputField}>
                <Label htmlFor="wemail">Email Address</Label>
                <Input
                  id="wemail"
                  name="wemail"
                  value={values.wemail}
                  invalid={
                    touched.wemail &&
                    Object.prototype.hasOwnProperty.call(errors, 'wemail')
                  }
                  onChange={handleChange}
                  disabled={isSubmitting || !!prefilledEmail}
                  onBlur={handleBlur}
                  className="pii-encrypt"
                />
                {touched.wemail && errors.wemail && (
                  <span css={buyerRegistrationPanelStyles.invalid}>
                    {errors.wemail}
                  </span>
                )}
              </div>
            </Flex>
            <Flex>
              <div css={buyerRegistrationPanelStyles.inputField}>
                <Label htmlFor="phone">Phone Number</Label>
                <PhoneNumber
                  id="phone"
                  specialLabel=""
                  name="phone"
                  invalid={
                    touched.phone &&
                    Object.prototype.hasOwnProperty.call(errors, 'phone')
                  }
                  value={values.phone}
                  handleChange={(value) => {
                    if (value === undefined) {
                      value = '';
                    }
                    setFieldValue('phone', value);
                  }}
                  disabled={isSubmitting}
                  handleBlur={handleBlur}
                  className="pii-encrypt"
                />
                {touched.phone && errors.phone && (
                  <span css={buyerRegistrationPanelStyles.invalid}>
                    {errors.phone}
                  </span>
                )}
              </div>
              <div css={buyerRegistrationPanelStyles.inputField}>
                <Label size="medium" htmlFor="zipCode">
                  Zip Code (optional)
                </Label>
                <Input
                  id="zipCode"
                  name="zipCode"
                  type="text"
                  value={values.zipCode}
                  onChange={handleChange}
                  invalid={
                    touched.zipCode &&
                    Object.prototype.hasOwnProperty.call(errors, 'zipCode')
                  }
                  disabled={isSubmitting}
                  onBlur={handleBlur}
                />
                {touched.zipCode && errors.zipCode && (
                  <span css={buyerRegistrationPanelStyles.invalid}>
                    {errors.zipCode}
                  </span>
                )}
              </div>
            </Flex>
            <Flex>
              {!hasPassword && (
                <div css={buyerRegistrationPanelStyles.inputField}>
                  <Label htmlFor="wpassword">Password</Label>
                  <Input
                    id="wpassword"
                    type="password"
                    textIndicators
                    showPassword
                    value={values.wpassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    invalid={
                      touched.wpassword &&
                      Object.prototype.hasOwnProperty.call(errors, 'wpassword')
                    }
                  />
                  {touched.wpassword && errors.wpassword && (
                    <div css={buyerRegistrationPanelStyles.invalid}>
                      {errors.wpassword}
                    </div>
                  )}
                  <ul css={buyerRegistrationPanelStyles.passwordRequirements}>
                    <li>Must be at least 8 characters long</li>
                    <li>At least 3 of the following:</li>
                    <ul
                      css={
                        buyerRegistrationPanelStyles.nestedPasswordRequirements
                      }
                    >
                      <li>Lower case letter (a-z)</li>
                      <li>Upper case letter (A-Z)</li>
                      <li>Number (0-9)</li>
                      <li>Special character (!@#$%^&*)</li>
                    </ul>
                  </ul>
                </div>
              )}
              <div css={buyerRegistrationPanelStyles.inputField}>
                <CustomerRole
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  disabled={isSubmitting}
                  customerRoleValue={values.customerRole}
                  customerRoleOtherValue={values.customerRoleOther}
                />
              </div>
            </Flex>
            <div css={buyerRegistrationPanelStyles.agreements}>
              <span>
                By registering, I agree to Xometry’s&nbsp;
                <Link
                  href="https://www.xometry.com/service-agreement"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Customer Service Agreement
                </Link>
                ,&nbsp;
                <Link
                  href="http://www.xometry.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  General Terms and Conditions
                </Link>
                , and&nbsp;
                <Link
                  href="https://www.xometry.com/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </Link>
                .
              </span>
            </div>
            <div css={buyerRegistrationPanelStyles.actionBar}>
              <Button
                type="submit"
                kind="primary"
                scale="large"
                disabled={isSubmitting}
                onClick={(...args) => {
                  if (Object.keys(errors).length || !values.termsAccepted) {
                    setTouched({
                      companyName: true,
                      firstName: true,
                      lastName: true,
                      phone: true,
                      wemail: true,
                      wpassword: true,
                      zipCode: true,
                      termsAccepted: true,
                    });
                    return;
                  }
                  handleSubmit(...args);
                }}
              >
                {isSubmitting ? (
                  <Flow scale="panel" kind="disabled" />
                ) : (
                  'Sign Up'
                )}
              </Button>
            </div>
          </Panel>
        </Form>
      )}
    />
  );
};

BuyerRegistrationPanel.propTypes = {
  anonFirst: PropTypes.string,
  anonLast: PropTypes.string,
  anonPhoneNumber: PropTypes.string,
  currentUserHasPassword: PropTypes.boolean,
  destination: PropTypes.string,
  handleRedirect: PropTypes.func,
  handleRegister: PropTypes.string,
  prefilledEmail: PropTypes.string,
  senderName: PropTypes.string,
  temporaryPasswordKey: PropTypes.string,
};

export default BuyerRegistrationPanel;
